import { Link } from 'react-router';
import { timeZoneLabel } from '@ruby-cms/shared';
import { useQuery } from '@tanstack/react-query';
import { api } from '../api/client';
import { useAuth } from '../auth/AuthContext';
import { PageHeader, ErrorState, BlockSkeleton } from '../components/states';
import { Money, When } from '../components/format';
import { Alerts, Columns, InsightBar, ScopeNote, Tile, ddmm, fmt, grainOf, periodRow, useInsightFilters, type Cmp, type Kind, type Scope } from '../components/Insights';
import { GRAIN_LABEL, PARTIAL_NOTE, PERIOD_WORD, periodTick } from '../components/Analytics';

// Screen 1 of the KPI catalogue: the executive overview. Every tile carries the three comparisons and
// its 30-day line; the figures come from GET /api/admin/insights/overview (formulas in docs/cms-golive).

type Tiles = {
  from: string; to: string; turnover: string; ggr: string; hold: number | null; bonus: string; commission: string; fees: string; ngr: string;
  deposits: string; withdrawals: string; netDeposit: string; ftd: number; registrations: number; bettors: number; arpu: number | null; ccuPeak: number | null;
};
type Day = Omit<Tiles, 'from' | 'to'> & { day: string; partial?: boolean };
type Overview = {
  from: string; to: string; scope: Scope; current: Tiles; compare: Cmp<Tiles>; trend: Day[];
  queue: { pendingWithdrawals: number; pendingAmount: string; oldestPending: string | null; unackedAlerts: number };
  statsLagMinutes: number | null;
  alerts: { kind: string; day?: string; value: number | null; reference?: number }[];
  thresholds: { ggrDropPct: number; statsLagMinutes: number };
};

// MKT accounts (docs/specs/mkt-tach-quy.md): left out of every figure above, shown apart (GET /api/admin/insights/mkt)
type MktMoney = { balance: string; stake: string; payout: string; refund: string; house: string; credits: string };
type Mkt = { total: MktMoney & { count: number }; accounts: (MktMoney & { userId: number; name: string })[] };

function MktBlock({ query }: { query: Record<string, string> }) {
  const q = useQuery({ queryKey: ['insights', 'mkt', query], queryFn: () => api.get<Mkt>('/api/admin/insights/mkt', query) });
  const d = q.data;
  return (
    <section className="section insight-group">
      <h2 className="insight-group__title">Tài khoản MKT (không tính vào các số trên)</h2>
      {q.isLoading ? <BlockSkeleton lines={3} /> : q.error ? <ErrorState error={q.error} onRetry={() => q.refetch()} /> : !d ? null : !d.total.count ? (
        <p className="muted">Chưa có tài khoản MKT. Founder đánh dấu ở trang người chơi; tiền cược và thắng của tài khoản MKT không vào quỹ game.</p>
      ) : (
        <>
          <div className="kpis insight-kpis">
            <div className="kpi"><span className="kpi__label">Số tài khoản</span><span className="kpi__value num">{d.total.count.toLocaleString('vi-VN')}</span></div>
            <div className="kpi"><span className="kpi__label">Doanh số cược</span><span className="kpi__value"><Money v={String(BigInt(d.total.stake) - BigInt(d.total.refund))} /></span><span className="kpi__hint">Cược - hoàn cược</span></div>
            <div className="kpi"><span className="kpi__label">Nhà cái lãi với MKT</span><span className="kpi__value"><Money v={d.total.house} signed /></span><span className="kpi__hint">Cược - trả thưởng - hoàn</span></div>
            <div className="kpi"><span className="kpi__label">Tiền cộng / trừ qua CMS</span><span className="kpi__value"><Money v={d.total.credits} signed /></span><span className="kpi__hint">Nạp, rút, điều chỉnh, chuyển tiền</span></div>
            <div className="kpi"><span className="kpi__label">Số dư hiện tại</span><span className="kpi__value"><Money v={d.total.balance} /></span></div>
          </div>
          <details className="insight-table-view">
            <summary>Xem từng tài khoản</summary>
            <div className="table-scroll">
              <table className="plain">
                <thead><tr><th>Tài khoản</th><th className="r">Doanh số</th><th className="r">Trả thưởng</th><th className="r">Nhà cái lãi</th><th className="r">Cộng / trừ CMS</th><th className="r">Số dư</th></tr></thead>
                <tbody>{d.accounts.map((a) => (
                  <tr key={a.userId}>
                    <td className="is-title"><Link className="cell-link" to={`/users/${a.userId}`}>{a.name}</Link></td>
                    <td className="r" data-label="Doanh số"><Money v={String(BigInt(a.stake) - BigInt(a.refund))} /></td>
                    <td className="r" data-label="Trả thưởng"><Money v={a.payout} /></td>
                    <td className="r" data-label="Nhà cái lãi"><Money v={a.house} signed /></td>
                    <td className="r" data-label="Cộng / trừ CMS"><Money v={a.credits} signed /></td>
                    <td className="r" data-label="Số dư"><Money v={a.balance} /></td>
                  </tr>
                ))}</tbody>
              </table>
            </div>
          </details>
        </>
      )}
    </section>
  );
}

const GROUPS: { title: string; tiles: [keyof Day & keyof Tiles, string, Kind, string?][] }[] = [
  { title: 'Doanh thu', tiles: [
    ['turnover', 'Doanh số cược', 'money', 'Cược - hoàn cược'],
    ['ggr', 'Lãi nhà cái (GGR)', 'money', 'Cược - trả thưởng - hoàn'],
    ['hold', 'Tỷ lệ giữ lại', 'pct', 'GGR / doanh số'],
    ['ngr', 'Lãi ròng (NGR)', 'money', 'GGR - thưởng - hoa hồng - phí cổng'],
  ] },
  { title: 'Tiền', tiles: [
    ['deposits', 'Tiền nạp', 'money'],
    ['withdrawals', 'Tiền rút đã chi', 'money'],
    ['netDeposit', 'Nạp ròng', 'money', 'Nạp - rút'],
  ] },
  { title: 'Người chơi', tiles: [
    ['ftd', 'Nạp lần đầu (FTD)', 'count'],
    ['registrations', 'Đăng ký mới', 'count'],
    ['bettors', 'Người có cược', 'count'],
    ['arpu', 'GGR mỗi người cược', 'money', 'ARPU'],
    ['ccuPeak', 'Online cao nhất', 'count', 'Người thật cùng lúc, cả hệ thống'],
  ] },
];

export function InsightOverviewPage() {
  const { can } = useAuth();
  const f = useInsightFilters();
  const q = useQuery({ queryKey: ['insights', 'overview', f.query], queryFn: () => api.get<Overview>('/api/admin/insights/overview', f.query) });
  const d = q.data;
  const grain = grainOf(f);
  const word = PERIOD_WORD[grain];
  const span = grain === 'day' ? '30 ngày' : grain === 'week' ? '12 tuần' : '12 tháng';
  const alerts = (d?.alerts ?? []).map((a) => a.kind === 'ggrDrop'
    ? { key: 'ggr', title: 'Lãi nhà cái giảm mạnh', body: `Ngày ${ddmm(a.day!)} lãi ${fmt(a.value, 'money')}, dưới ${d!.thresholds.ggrDropPct}% trung bình cùng thứ 4 tuần trước (${fmt(a.reference, 'money')}).` }
    : { key: 'lag', title: 'Số liệu báo cáo đang trễ', body: `Dòng giao dịch cũ nhất chưa tổng hợp đã ${a.value} phút (ngưỡng ${d!.thresholds.statsLagMinutes} phút). Các con số có thể thiếu phần mới nhất.` });
  return (
    <div className="page">
      <PageHeader title="Tổng quan điều hành" lead={`Doanh thu, dòng tiền, người chơi và hàng chờ theo ngày, ${timeZoneLabel()}; tài khoản thử, nhân viên, bot và tài khoản MKT không tính (MKT xem riêng bên dưới). Mỗi ô so với hôm trước, cùng thứ tuần trước và lũy kế tháng so cùng kỳ tháng trước.`} />
      <InsightBar id="io" f={f} exportPath="/api/admin/insights/overview" exportSections={[['tiles', 'Các chỉ số và so sánh'], ['trend', `Từng ${word}, ${span}`]]} />
      <ScopeNote scope={d?.scope} />
      {q.isLoading ? <BlockSkeleton lines={10} /> : q.error ? <ErrorState error={q.error} onRetry={() => q.refetch()} /> : d ? (
        <>
          <Alerts items={alerts} />
          {GROUPS.map((g) => (
            <section key={g.title} className="section insight-group">
              <h2 className="insight-group__title">{g.title}</h2>
              <div className="kpis insight-kpis">
                {g.tiles.map(([k, label, kind, hint], i) => (
                  <Tile key={k} label={label} k={k} kind={kind} cur={d.current} cmp={d.compare} hint={hint} lead={g.title === 'Doanh thu' && i === 1}
                    warn={k === 'ggr' && d.alerts.some((a) => a.kind === 'ggrDrop')}
                    spark={d.trend.map((t) => ({ day: t.day, v: t[k] === null ? null : Number(t[k]) }))} sparkSpan={span} sparkDay={(p) => periodRow(p, grain, d.trend.find((t) => t.day === p)?.partial)} />
                ))}
              </div>
            </section>
          ))}
          <section className="section insight-group">
            <h2 className="insight-group__title">Hàng chờ và rủi ro (hiện tại)</h2>
            <div className="kpis insight-kpis">
              <div className={'kpi' + (d.queue.pendingWithdrawals ? ' insight-tile--warn' : '')}>
                <span className="kpi__label">Lệnh rút đang chờ</span>
                <span className="kpi__value num">{d.queue.pendingWithdrawals.toLocaleString('vi-VN')}</span>
                <span className="kpi__hint"><Money v={d.queue.pendingAmount} unit />{d.queue.oldestPending ? <>, cũ nhất <When v={d.queue.oldestPending} /></> : null}</span>
                {can('wallet.view') ? <Link className="cell-link" to="/wallet/withdrawals">Mở hàng rút</Link> : null}
              </div>
              <div className={'kpi' + (d.queue.unackedAlerts ? ' insight-tile--warn' : '')}>
                <span className="kpi__label">Thắng lớn chưa xem</span>
                <span className="kpi__value num">{d.queue.unackedAlerts.toLocaleString('vi-VN')}</span>
                <Link className="cell-link" to="/alerts">Mở cảnh báo</Link>
              </div>
              <div className={'kpi' + (d.alerts.some((a) => a.kind === 'statsLag') ? ' insight-tile--warn' : '')}>
                <span className="kpi__label">Độ trễ số liệu báo cáo</span>
                <span className="kpi__value num">{d.statsLagMinutes === null ? '-' : `${d.statsLagMinutes.toLocaleString('vi-VN')} phút`}</span>
                <span className="kpi__hint">Ngưỡng báo {d.thresholds.statsLagMinutes} phút (Cài đặt)</span>
              </div>
            </div>
          </section>
          <MktBlock query={{ from: d.from, to: d.to }} />
          <section className="section panel">
            <div className="panel__head"><h2>Lãi nhà cái và lãi ròng, {span} đến {ddmm(d.to)}</h2></div>
            <Columns title={`Lãi nhà cái và lãi ròng theo ${word}`} kind="money" labels={d.trend.map((t) => periodTick(t.day, grain, t.partial))}
              series={[{ name: 'Lãi nhà cái', values: d.trend.map((t) => Number(t.ggr)) }, { name: 'Lãi ròng', values: d.trend.map((t) => Number(t.ngr)) }]} />
            <details className="insight-table-view">
              <summary>Xem bảng số {span}</summary>
              <div className="table-scroll">
                <table className="plain">
                  <thead><tr><th>{GRAIN_LABEL[grain]}</th><th className="r">Doanh số</th><th className="r">GGR</th><th className="r">Giữ lại</th><th className="r">Thưởng</th><th className="r">Hoa hồng</th><th className="r">Phí cổng</th><th className="r">NGR</th><th className="r">Nạp</th><th className="r">Rút</th><th className="r">FTD</th><th className="r">Đăng ký</th><th className="r">Người cược</th><th className="r">Online cao nhất</th></tr></thead>
                  <tbody>{[...d.trend].reverse().map((t) => (
                    <tr key={t.day}>
                      <td className="num is-title">{periodRow(t.day, grain, t.partial)}</td>
                      <td className="r" data-label="Doanh số"><Money v={t.turnover} /></td>
                      <td className="r" data-label="GGR"><Money v={t.ggr} signed /></td>
                      <td className="r num" data-label="Giữ lại">{fmt(t.hold, 'pct')}</td>
                      <td className="r" data-label="Thưởng"><Money v={t.bonus} /></td>
                      <td className="r" data-label="Hoa hồng"><Money v={t.commission} /></td>
                      <td className="r" data-label="Phí cổng"><Money v={t.fees} /></td>
                      <td className="r" data-label="NGR"><Money v={t.ngr} signed /></td>
                      <td className="r" data-label="Nạp"><Money v={t.deposits} /></td>
                      <td className="r" data-label="Rút"><Money v={t.withdrawals} /></td>
                      <td className="r num" data-label="FTD">{t.ftd}</td>
                      <td className="r num" data-label="Đăng ký">{t.registrations}</td>
                      <td className="r num" data-label="Người cược">{t.bettors}</td>
                      <td className="r num" data-label="Online cao nhất">{t.ccuPeak ?? '-'}</td>
                    </tr>
                  ))}</tbody>
                </table>
              </div>
            </details>
          </section>
          <p className="muted note">
            Chi tiết: <Link to={`/insights/cashier?${new URLSearchParams(f.query)}`}>Thu ngân</Link>, <Link to={`/insights/games?${new URLSearchParams(f.query)}`}>Hiệu quả game</Link>,{' '}
            <Link to={`/insights/promotions?${new URLSearchParams(f.query)}`}>Hiệu quả khuyến mãi</Link>. Người có cược đếm mỗi tài khoản một lần trong cả khoảng ngày, và một lần trong mỗi {word} của bảng; online cao nhất là số cao nhất trong các ngày của khoảng và không lọc theo đại lý.{grain !== 'day' ? ' ' + PARTIAL_NOTE : ''}
          </p>
        </>
      ) : null}
    </div>
  );
}
